import type { HelpStep } from "../../help/types";

/**
 * Guide steps shared by several page guides: the benefit packages pages (Company profile and HR),
 * and the implementation page, which opens the same package and documents dialogs.
 *
 * Pure data (only `import type`). Deliberately NOT named `help.<x>.ts`: the help scripts treat
 * that pattern as a page guide, and this module is only a building block spread into the guides.
 *
 * The steps use these context keys; every guide that spreads them must define them:
 * - "package-dialog", "package-dialog-add", "package-dialog-edit" (how to open the package dialog
 *   differs per page)
 * - "documents-dialog" (how to open the documents dialog differs per page)
 * - "documents-queued", "documents-expanded" (identical everywhere, see DOCUMENTS_CONTEXTS)
 * - "list-view" (packages pages only, see LIST_VIEW_CONTEXT)
 */

export const PACKAGE_DIALOG_DETAILS = "Package dialog: details and price";
export const PACKAGE_DIALOG_RULES = "Package dialog: eligibility and enrollment";
export const DOCUMENTS_DIALOG = "Documents dialog";

export const LIST_VIEW_CONTEXT =
  'Open the settings menu (gear icon in the header) and pick the list icon under "View Mode". The choice is saved for your user and applies to all benefit pages.';

export const DOCUMENTS_CONTEXTS: Record<string, string> = {
  "documents-queued":
    'In the documents dialog, click "Upload" and choose one or more files. They wait in a list below the button until you save them.',
  "documents-expanded":
    "In the documents dialog, click the edit icon at the right end of a saved document's row to expand its details.",
};

/** The add/edit package dialog (`components/PackageDialog`). */
export const packageDialogSteps: HelpStep[] = [
  {
    selector: '[data-help="package-dialog-name"]',
    section: PACKAGE_DIALOG_DETAILS,
    context: "package-dialog",
    title: "Name the package",
    body: "The name employees and HR see on the package card, for example a plan tier. Required. The server refuses a second current package with the same name in the same implementation.",
  },
  {
    selector: '[data-help="package-dialog-carrier"]',
    section: PACKAGE_DIALOG_DETAILS,
    context: "package-dialog",
    title: "Check the carrier",
    body: "The insurer or provider of this package. A new package starts with the carrier of its implementation; change it only if this package is placed with a different carrier. Required.",
  },
  {
    selector: '[data-help="package-dialog-price"]',
    section: PACKAGE_DIALOG_DETAILS,
    context: "package-dialog",
    title: "Enter the premium or cost",
    body: 'The full price of the package. The currency in brackets comes from the implementation and cannot be changed here. Required. With value type "Amount", the company and employee shares must add up to exactly this figure.',
  },
  {
    selector: '[data-help="package-dialog-payment-method"]',
    section: PACKAGE_DIALOG_DETAILS,
    context: "package-dialog",
    title: "Choose the payment method",
    body: "Pick how the premium is paid from the list of payment methods maintained in HBH. Required. The value is shown in the Payment Method column of the list view.",
  },
  {
    selector: '[data-help="package-dialog-value-type"]',
    section: PACKAGE_DIALOG_DETAILS,
    context: "package-dialog",
    title: "Split the cost by percentage or amount",
    body: 'Decides how the two "Paid by" fields are read. "Percentage": both shares are percentages that must total 100. "Amount": both shares are sums in the implementation currency that must total the premium. Required.',
  },
  {
    selector: '[data-help="package-dialog-paid-by-company"]',
    section: PACKAGE_DIALOG_DETAILS,
    context: "package-dialog",
    title: "Enter the company share",
    body: 'The part the employer pays. Typing here fills "Paid by Employee" with the remainder. If the shares do not add up to 100 % or to the premium, both fields show "Payments must be equal to package price" and the server refuses to save.',
  },
  {
    selector: '[data-help="package-dialog-paid-by-employee"]',
    section: PACKAGE_DIALOG_DETAILS,
    context: "package-dialog",
    title: "Enter the employee share",
    body: 'The part the employee pays. Typing here recalculates "Paid by Company" the same way, so you normally fill in only one of the two fields. Required.',
  },
  {
    selector: '[data-help="package-dialog-payment-type"]',
    section: PACKAGE_DIALOG_DETAILS,
    context: "package-dialog",
    title: "Choose the payment type",
    body: 'How the money is collected: direct payment, premium collection or salary deduction. Required. With salary deduction the server refuses "Is Auto Enrollment", and every enrollment into the package needs approval.',
  },
  {
    selector: '[data-help="package-dialog-age-from"]',
    section: PACKAGE_DIALOG_DETAILS,
    context: "package-dialog",
    title: "Set a minimum age",
    body: "Optional lower age limit for the covered person (employee or dependant), shown next to the package name on the card. It blocks self-enrollment in the employee app for younger people; enrollments entered by HR are not checked against it.",
  },
  {
    selector: '[data-help="package-dialog-age-to"]',
    section: PACKAGE_DIALOG_DETAILS,
    context: "package-dialog",
    title: "Set a maximum age",
    body: 'Optional upper age limit, applied like "Age Limit From": employees or dependants above it cannot enroll themselves in the employee app, while HR can still enroll them.',
  },
  {
    selector: '[data-help="package-dialog-description"]',
    section: PACKAGE_DIALOG_DETAILS,
    context: "package-dialog",
    title: "Describe the package",
    body: "What the package covers, in the words employees will read on the card and in the employee app. Required, 2 to 4000 characters.",
  },
  {
    selector: '[data-help="package-dialog-eligible-from"]',
    section: PACKAGE_DIALOG_RULES,
    context: "package-dialog",
    title: "Define when eligibility starts",
    body: '"Eligible From Date" is the reference date (start of employment or enrollment, start or end of policy, end of employment), "Eligible From" the unit and "Eligible From Value" the number of units. "Immediate" needs no value; for a date in the next month the value is the day. The calculated date is never before the policy start or the employment date.',
  },
  {
    selector: '[data-help="package-dialog-eligible-to"]',
    section: PACKAGE_DIALOG_RULES,
    context: "package-dialog",
    title: "Define when eligibility ends",
    body: 'The same three-part pattern with "Eligible To Date", "Eligible To" and "Eligible To Value". When the reference date is an end date (end of employment or end of policy), the units are counted backwards from it. A calculated end after the policy end is cut back to the policy end.',
  },
  {
    selector: '[data-help="package-dialog-cancellation"]',
    section: PACKAGE_DIALOG_RULES,
    context: "package-dialog",
    title: "Define from when cancellation is possible",
    body: '"Cancellation From Date", "Cancellation From" and "Cancellation Value" give the earliest date an employee can cancel the package. With "start of employment or enrollment" the count starts at the enrollment date, and the result is never earlier than the eligibility date.',
  },
  {
    selector: '[data-help="package-dialog-offboarding"]',
    section: PACKAGE_DIALOG_RULES,
    context: "package-dialog",
    title: "Define when cover ends after leaving",
    body: '"Offboarding From Date", "Offboarding From" and "Offboarding Value" calculate the offboarding date when an employee leaves the company. The result is never before the exit date and never after the policy end.',
  },
  {
    selector: '[data-help="package-dialog-auto-enrollment"]',
    section: PACKAGE_DIALOG_RULES,
    context: "package-dialog",
    title: "Mark the package for auto-enrollment",
    body: 'Flags the package as auto-enrollment; its card then shows a green sync icon. Disabled when the implementation needs consents, and ticking it clears "Requires Enrollment Approval". The server also refuses it together with payment type salary deduction.',
  },
  {
    selector: '[data-help="package-dialog-upgradeable"]',
    section: PACKAGE_DIALOG_RULES,
    context: "package-dialog",
    title: "Allow upgrading to this package",
    body: "Lets an employee enroll in this package while already holding an active package of the same implementation, keeping the existing benefits during the move. If such an upgrade enrollment is rejected, the enrollment it replaced is restored. The card shows a rocket icon.",
  },
  {
    selector: '[data-help="package-dialog-enrollment-approval"]',
    section: PACKAGE_DIALOG_RULES,
    context: "package-dialog",
    title: "Require approval for enrollments",
    body: 'New enrollments wait for approval before they count. Ticked and locked when the implementation needs consents; unavailable while "Is Auto Enrollment" is ticked. With payment type salary deduction, enrollments need approval whatever this box says.',
  },
  {
    selector: '[data-help="package-dialog-cancellation-approval"]',
    section: PACKAGE_DIALOG_RULES,
    context: "package-dialog",
    title: "Require approval for cancellations",
    body: "When ticked, an employee's cancellation of this package waits for approval before it takes effect. Such packages show a red document icon on their card.",
  },
  {
    selector: '[data-help="package-dialog-attachments"]',
    section: PACKAGE_DIALOG_RULES,
    context: "package-dialog-add",
    title: "Attach documents to a new package",
    body: 'Click "Upload" to add files such as policy terms or brochures; they are saved together with the new package. Each file has an eye icon that hides it from employees or shows it, and a bin icon that drops it before saving.',
  },
  {
    selector: '[data-help="package-dialog-documents"]',
    section: PACKAGE_DIALOG_RULES,
    context: "package-dialog-edit",
    title: "Manage the documents of a package",
    body: 'Shows how many documents the package has and opens the documents dialog. Changes made there (upload, edit, remove) are saved immediately, not with the "Save" button of this form.',
  },
  {
    selector: '[data-help="package-dialog-save"]',
    section: PACKAGE_DIALOG_RULES,
    context: "package-dialog",
    title: "Save the package",
    body: "Checks the required fields and the payment split, then creates or updates the package and closes the dialog. If the server refuses the data, an error message appears and the dialog stays open with your input.",
  },
];

/** The documents dialog (`components/ManageAttachmentsDialog`), opened from cards and rows. */
export const documentsDialogSteps: HelpStep[] = [
  {
    selector: '[data-help="attachments-upload"]',
    section: DOCUMENTS_DIALOG,
    context: "documents-dialog",
    title: "Upload new documents",
    body: 'Opens the file picker; you can choose several files at once. A file with the same name as an existing document is skipped. Chosen files are not stored yet: save them one by one or with "Save All".',
  },
  {
    selector: '[data-help="attachments-queued-visibility"]',
    section: DOCUMENTS_DIALOG,
    context: "documents-queued",
    title: "Hide a new document from employees",
    body: 'The eye icon on a waiting file switches it between "Is Visible For Employee" and "Is Hidden For Employee" before you save it. Hidden documents are not shown in the employee app. Not offered for benefit-category documents.',
  },
  {
    selector: '[data-help="attachments-queued-save"]',
    section: DOCUMENTS_DIALOG,
    context: "documents-queued",
    title: "Save one new document",
    body: "Uploads this file with its visibility setting and removes it from the waiting list. The bin icon next to it drops the file without uploading it.",
  },
  {
    selector: '[data-help="attachments-save-all"]',
    section: DOCUMENTS_DIALOG,
    context: "documents-queued",
    title: "Save all new documents",
    body: 'Appears when two or more files are waiting, and uploads all of them in one go. "Clear" next to it empties the waiting list without uploading anything.',
  },
  {
    selector: '[data-help="attachments-open"]',
    section: DOCUMENTS_DIALOG,
    context: "documents-dialog",
    optional: true,
    title: "Open a saved document",
    body: "Saved documents are listed by file name; click the name to open the file in a new browser tab. The eye icon at the end of the row shows whether employees can see the document.",
  },
  {
    selector: '[data-help="attachments-edit-toggle"]',
    section: DOCUMENTS_DIALOG,
    context: "documents-dialog",
    optional: true,
    title: "Expand the details of a document",
    body: "Opens the summary, abstract and visibility settings of the document below its row. Click the icon again to collapse them.",
  },
  {
    selector: '[data-help="attachments-summary"]',
    section: DOCUMENTS_DIALOG,
    context: "documents-expanded",
    title: "Write a document summary",
    body: 'Optional short summary of the document, up to 4000 characters. It is stored when you click "Save Document".',
  },
  {
    selector: '[data-help="attachments-abstract"]',
    section: DOCUMENTS_DIALOG,
    context: "documents-expanded",
    title: "Write a document abstract",
    body: 'Optional abstract with the key points of the document, up to 4000 characters, stored with "Save Document".',
  },
  {
    selector: '[data-help="attachments-hidden"]',
    section: DOCUMENTS_DIALOG,
    context: "documents-expanded",
    title: "Change who can see a saved document",
    body: 'Tick "Is Hidden For Employee" to hide the document in the employee app, untick it to show the document again. Stored with "Save Document". Not offered for benefit-category documents.',
  },
  {
    selector: '[data-help="attachments-remove"]',
    section: DOCUMENTS_DIALOG,
    context: "documents-expanded",
    title: "Remove a document",
    body: '"Remove Document" deletes the document after you confirm. The removal takes effect at once; there is no undo.',
  },
  {
    selector: '[data-help="attachments-save"]',
    section: DOCUMENTS_DIALOG,
    context: "documents-expanded",
    title: "Save changes to a document",
    body: '"Save Document" stores the summary, abstract and visibility of this document. It stays disabled until you change one of them.',
  },
];

/* ---- Benefit packages pages (Company profile and HR) ------------------------------------ */

export const PACKAGES_GETTING_AROUND = "Getting around";
export const PACKAGES_TOOLBAR = "Toolbar";
export const PACKAGES_READING_CARDS = "Reading the package cards";
export const PACKAGES_CARD_ACTIONS = "Working with a package card";
export const PACKAGES_LIST_VIEW = "List view";

/** Refresh and search: the same on both packages pages. The breadcrumb differs per page. */
export const packagesFindSteps: HelpStep[] = [
  {
    selector: '[data-help="packages-refresh"]',
    section: PACKAGES_GETTING_AROUND,
    title: "Reload the packages",
    body: "Fetches the packages of this implementation again from the server. Use it when a colleague may have changed them or after an import.",
  },
  {
    selector: '[data-help="packages-search"]',
    section: PACKAGES_GETTING_AROUND,
    title: "Search packages by name",
    body: "Filters the cards or table rows to packages whose name contains the text. The number at the right end of the bar is the total number of packages in the implementation. Disabled when the implementation has no packages.",
  },
];

export const packagesToolbarSteps: HelpStep[] = [
  {
    selector: '[data-help="benefit-cards-save-order"]',
    section: PACKAGES_TOOLBAR,
    title: "Save your card order",
    body: 'In grid view you can drag the package cards into any order; "Save Order" stores that order for your user, so it is not shared with colleagues. Disabled while a search is active.',
  },
  {
    selector: '[data-help="packages-add"]',
    section: PACKAGES_TOOLBAR,
    title: "Add a package to the implementation",
    body: '"Add Package" opens the package dialog for this implementation. Only admin roles (global, country or company admin) can use it; for read-only and HR roles the button is disabled.',
  },
];

/**
 * What every package card shows. Each page adds its own step for the card itself, because a
 * double-click on the card picture does something different on the HR page.
 */
export const packageCardSteps: HelpStep[] = [
  {
    selector: '[data-help="benefit-card-tags"]',
    section: PACKAGES_READING_CARDS,
    optional: true,
    title: "See how many people are enrolled",
    body: "The person tag shows enrolled / eligible people for this package, employees and dependants together. Hover it for the explanation.",
  },
  {
    selector: '[data-help="benefit-card-auto-enrollment"]',
    section: PACKAGES_READING_CARDS,
    optional: true,
    minRole: "admin",
    title: "Recognise an auto-enrollment package",
    body: 'A green sync icon in the card footer means the package has "Is Auto Enrollment" set. The status icons are only shown to roles that can edit packages.',
  },
  {
    selector: '[data-help="benefit-card-upgradeable"]',
    section: PACKAGES_READING_CARDS,
    optional: true,
    minRole: "admin",
    title: "Recognise an upgradeable package",
    body: "A green rocket icon means employees may enroll in this package even with an active package of the same implementation. Hover it for the full explanation.",
  },
  {
    selector: '[data-help="benefit-card-enrollment-approval"]',
    section: PACKAGES_READING_CARDS,
    optional: true,
    minRole: "admin",
    title: "See that enrollments need approval",
    body: "A green document icon with a check mark means new enrollments into this package wait for approval before they count.",
  },
  {
    selector: '[data-help="benefit-card-cancellation-approval"]',
    section: PACKAGES_READING_CARDS,
    optional: true,
    minRole: "admin",
    title: "See that cancellations need approval",
    body: "A red document icon with a cross means cancellations of this package wait for approval before they take effect.",
  },
];

export const packageCardActionSteps: HelpStep[] = [
  {
    selector: '[data-help="benefit-card-documents"]',
    section: PACKAGES_CARD_ACTIONS,
    optional: true,
    minRole: "admin",
    title: "Manage the documents of a package",
    body: "Opens the documents dialog of this package, where you upload, describe, hide or remove files. Changes there are saved immediately.",
  },
  {
    selector: '[data-help="benefit-card-edit"]',
    section: PACKAGES_CARD_ACTIONS,
    optional: true,
    minRole: "admin",
    title: "Edit a package",
    body: "Opens the package dialog with the current values. Saving creates a new version of the package; the history is kept on the server.",
  },
  {
    selector: '[data-help="benefit-card-delete"]',
    section: PACKAGES_CARD_ACTIONS,
    optional: true,
    minRole: "superadmin",
    title: "Delete a package",
    body: "Deletes the package after you confirm, together with its documents and all employee enrollments in it. The server carries out deletes only for GrECo global administrators.",
  },
];

/** The table shown in list view. The HR page adds its own "open employees" step. */
export const packageListSteps: HelpStep[] = [
  {
    selector: '[data-help="packages-table"]',
    section: PACKAGES_LIST_VIEW,
    context: "list-view",
    optional: true,
    title: "Edit packages in the table",
    body: "List view shows one row per package. Admin roles can change most values directly in the cells; the row then switches to Save and Undo buttons in the Actions column. Nothing is stored until you click Save on that row.",
  },
  {
    selector: '[data-help="packages-column-name"]',
    section: PACKAGES_LIST_VIEW,
    context: "list-view",
    optional: true,
    title: "Sort or rename packages",
    body: "Click a column header to sort by it. The Name cells are editable for admin roles; the name must stay unique within the implementation.",
  },
  {
    selector: '[data-help="packages-column-value-type"]',
    section: PACKAGES_LIST_VIEW,
    context: "list-view",
    optional: true,
    title: "Switch between percentage and amount",
    body: 'Value Type decides whether "Paid by Company" and "Paid by Employee" are percentages (total 100) or amounts (total equal to the premium).',
  },
  {
    selector: '[data-help="packages-column-price"]',
    section: PACKAGES_LIST_VIEW,
    context: "list-view",
    optional: true,
    title: "Change the premium",
    body: "Premium/Cost is the full price in the implementation currency. When you change it, check the two paid-by cells again: the shares must still add up.",
  },
  {
    selector: '[data-help="packages-column-paid-by"]',
    section: PACKAGES_LIST_VIEW,
    context: "list-view",
    optional: true,
    title: "Split the cost in the table",
    body: 'The "Paid by Company" and "Paid by Employee" cells hold the two shares. If they do not add up to 100 % or to the premium, the server refuses to save the row.',
  },
  {
    selector: '[data-help="packages-column-eligibility"]',
    section: PACKAGES_LIST_VIEW,
    context: "list-view",
    optional: true,
    title: "Check the eligibility rules",
    body: "From this column on, the table shows the eligibility, cancellation and offboarding rules as reference date, unit and value, the same fields as in the package dialog. Scroll right to see them all.",
  },
  {
    selector: '[data-help="packages-column-flags"]',
    section: PACKAGES_LIST_VIEW,
    context: "list-view",
    optional: true,
    title: "Set the enrollment options per row",
    body: "Is Auto Enrollment, Is Upgradeable and the two approval columns are yes/no choices. The same rules apply as in the dialog: no auto-enrollment with consents or salary deduction.",
  },
  {
    selector: '[data-help="package-row-documents"]',
    section: PACKAGES_LIST_VIEW,
    context: "list-view",
    optional: true,
    title: "Open the documents of a row",
    body: 'The Documents cell shows "Manage Documents" with the number of files, or "Add Documents" when there are none, and opens the documents dialog. Disabled for read-only and HR roles.',
  },
  {
    selector: '[data-help="package-row-edit"]',
    section: PACKAGES_LIST_VIEW,
    context: "list-view",
    optional: true,
    title: "Edit a row in the dialog",
    body: "The pencil icon opens the full package dialog for this row, including the documents. Disabled for read-only and HR roles.",
  },
  {
    selector: '[data-help="package-row-save"]',
    section: PACKAGES_LIST_VIEW,
    context: "list-view",
    optional: true,
    title: "Save changes made in a row",
    body: "Appears after you change a cell. Sends the row to the server; a success message confirms it. If a rule is broken (payment split, duplicate name), the server refuses and the row keeps your changes.",
  },
  {
    selector: '[data-help="package-row-undo"]',
    section: PACKAGES_LIST_VIEW,
    context: "list-view",
    optional: true,
    title: "Undo changes in a row",
    body: "Puts the row back to its last saved values without contacting the server. Appears next to Save once a cell has been changed.",
  },
  {
    selector: '[data-help="package-row-delete"]',
    section: PACKAGES_LIST_VIEW,
    context: "list-view",
    optional: true,
    title: "Delete a package from the table",
    body: "Deletes the package after you confirm, with its documents and employee enrollments. The icon is active for admin roles, but the server carries out deletes only for GrECo global administrators.",
  },
];
